import { useCallback, useEffect, useRef, useState } from 'react';
import { getMetaCampaigns } from '../../services/metaAdsService';
import { getMetaErrorMessage, readMetaErrorCode } from '../metaAdsConfig';
import { readMetaCampaigns } from '../metaCampaigns';
import { knownDeletedMetaCampaigns, rememberDeletedMetaCampaigns } from '../metaDeletedCampaigns';

const INITIAL = { loaded: false, loading: false, error: null, errorCode: null, period: null, totals: null, campaigns: [], hiddenCampaignsCount: 0,
  activeCampaignsCount: null, totalCampaignsCount: null, hiddenAdsCount: null,
  hasCampaignCounts: false, rawTotalCampaignsCount: null, deletedCampaignIds: [], deletedCountKnown: false,
  adsManagerColumns: null, adsManagerReachNote: null, totalsAdsManager: null, totalsAdsManagerByCurrency: [],
  dataStale: false, dataThrough: null, dataFreshnessText: null, lastSyncedAt: null, budgetAvailable: null,
  // Con qué filtro se cargó la lista que está en `campaigns` (null = todavía nada).
  loadedIncludeInactive: null };

/**
 * Campañas con anuncios y vínculos (`GET /getmetacampaigns`, período por
 * defecto de la API: 30 días). `includeInactive` → `include_inactive=1`.
 * `reload({ silent: true })` recarga sin skeleton (después de vincular).
 * `setCampaigns(fn)` para cambios optimistas. Las eliminadas en Meta no vienen en `campaigns`
 * ni cuentan en `totalCampaignsCount` (`metaDeletedCampaigns.js`). `loadedIncludeInactive`: el filtro de la
 * lista cargada (el buscador de Campañas no limpia la elección hasta que llega la del filtro nuevo).
 *
 * @param {{ ownerUserId?: string|null, enabled?: boolean, includeInactive?: boolean }} options
 */
export function useMetaCampaigns({ ownerUserId = null, enabled = true, includeInactive = false, period = null } = {}) {
  // Ajuste 10: `period` = `{ since, until }` del selector (sin él, el default de la API).
  const since = period?.since || '';
  const until = period?.until || '';
  const [state, setState] = useState(INITIAL);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    if (!silent) setState((prev) => ({ ...prev, loading: true, error: null, errorCode: null }));
    let res;
    try {
      res = await getMetaCampaigns({
        ownerUserId, includeInactive, period: since && until ? { since, until } : undefined, signal: controller.signal,
      });
    } catch (error) {
      // Cancelado (otro load lo reemplazó, se cerró el panel o StrictMode desmonta y
      // remonta en dev): no es un error ni se toca el estado. `safeRequest` solo deja
      // pasar AbortError; sin este catch quedaba como rechazo sin manejar (overlay de CRA).
      if (error?.name === 'AbortError') return null;
      throw error;
    }
    if (!mountedRef.current || controller.signal.aborted) return res;
    if (res?._httpOk) {
      const parsed = readMetaCampaigns(res, { includeInactive, knownDeleted: knownDeletedMetaCampaigns(ownerUserId) });
      if (parsed.deletedCountKnown) {
        rememberDeletedMetaCampaigns(ownerUserId, parsed.rawTotalCampaignsCount, parsed.deletedCampaignIds);
      }
      setState({ ...INITIAL, loaded: true, ...parsed, loadedIncludeInactive: includeInactive });
    } else if (!silent) {
      setState((prev) => ({
        ...prev, loaded: true, loading: false, error: getMetaErrorMessage(res), errorCode: readMetaErrorCode(res) || null, loadedIncludeInactive: includeInactive,
      }));
    }
    return res;
  }, [ownerUserId, includeInactive, since, until]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const setCampaigns = useCallback((update) => {
    setState((prev) => ({ ...prev, campaigns: typeof update === 'function' ? update(prev.campaigns) : update }));
  }, []);

  const reload = useCallback((options) => load(options), [load]);

  return { ...state, reload, setCampaigns };
}
